import { useLoaderData } from 'react-router';
import Select from 'client/components/GDS/Select/Select';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { filterActivityTypes } from 'client/utils/helpers/AssetTypes/AssetTypes';
import getTranslationOrEnglish from 'client/utils/i18n/i18nUtils';
import Fieldset from 'client/components/GDS/Fieldset/Fieldset';

const CHOOSE_OPTION = 'CHOOSE_OPTION';

/**
 * Displays the activity type and category drop downs.
 */
const EnquiryActivityType = ({
    onActivityTypeChange,
    activityType,
    errorMessage,
    categoryHint,
    valueHint,
    ...props
}) => {
    const intl = useIntl();
    const { activityTypes } = useLoaderData();
    const currentLocale = useSelector(state => state?.locale?.current);
    const typesByCategory = useMemo(() => filterActivityTypes(activityTypes, currentLocale),
        [activityTypes, currentLocale]);
    const [category, setCategory] = useState('');
    const categoryHintText = categoryHint ?? intl.formatMessage({ id: 'activityType.category.hint' });
    const valueHintText = valueHint ?? intl.formatMessage({ id: 'activityType.value.hint' });

    useEffect(() => {
        // on page load check if is activity type
        let selectedActivityType;
        if (activityType) {
            selectedActivityType = Object.values(typesByCategory)
                .flat()
                .find(value => value.systemId === activityType);
        }

        // set to existing category if exists, otherwise set defaults
        if (selectedActivityType) {
            setCategory(getTranslationOrEnglish(selectedActivityType.category, currentLocale));
        }
    }, []);

    useEffect(() => {
        const selectedActivityType = Object.values(typesByCategory)
            .flat()
            .find(value => value.systemId === activityType);
        if (selectedActivityType) {
            const activityTypeCategory = getTranslationOrEnglish(selectedActivityType.category, currentLocale);
            setCategory(activityTypeCategory);
        }
    }, [typesByCategory]);

    let activityTypeCategories = typesByCategory && Object.keys(typesByCategory);
    if (!activityTypeCategories || activityTypeCategories.length === 0) {
        return <FormattedMessage id={'activityType.missing'}/>;
    } else {
        activityTypeCategories = activityTypeCategories.sort((a, b) => a.localeCompare(b));
    }

    const getCategoryOption = (val) => <option key={val} value={val}>{val}</option>;
    const categoryOptions = activityTypeCategories.map(category => getCategoryOption(category));
    const getValueOption = (val) => <option key={val.systemId} value={val.systemId}>{getTranslationOrEnglish(val.value, currentLocale)}</option>;
    const valuesForActivity = typesByCategory[category];
    let valueOptions = valuesForActivity?.map(category => getValueOption(category));

    const handleCategoryChange = (e) => {
        // When category changed then set category and reset type value
        setCategory(e.target.value);
        onActivityTypeChange(CHOOSE_OPTION);
    };

    let secondDropDownLabel = intl.formatMessage({ id: 'activityType.chooseOption' });
    if (!valueOptions) {
        secondDropDownLabel = intl.formatMessage({ id: 'activityType.chooseCatFirst' });
    } else {
        valueOptions = valueOptions.sort((a, b) => a.props.children.localeCompare(b.props.children));
    }

    return <div className={'govuk-grid-row'} data-testid={props['data-testid']}>
        <div className="govuk-grid-column-full">
            <Fieldset errorMessage={errorMessage}>
                <div>
                    <Select
                        name='activityCategory'
                        value={category}
                        onChange={handleCategoryChange}
                        label={intl.formatMessage({ id: 'activityType.category' })}
                        description={categoryHintText}
                        data-testid={'activityType.category'}
                        id='activityType'
                    >
                        <option value={CHOOSE_OPTION}>{intl.formatMessage({ id: 'activityType.chooseOption' })}</option>
                        {categoryOptions}
                    </Select>
                    <Select
                        name='activityValue'
                        value={activityType}
                        onChange={e => onActivityTypeChange(e.target.value)}
                        label={intl.formatMessage({ id: 'activityType.value' })}
                        description={valueHintText}
                        data-testid={'activityType.type'}
                    >
                        <option value={CHOOSE_OPTION}>{secondDropDownLabel}</option>
                        {valueOptions}
                    </Select>
                </div>
            </Fieldset>
        </div>
    </div>;
};

EnquiryActivityType.propTypes = {
    'data-testid': PropTypes.string,
    errorMessage: PropTypes.string,
    activityType: PropTypes.string.isRequired,
    onActivityTypeChange: PropTypes.func.isRequired,
    categoryHint: PropTypes.string,
    valueHint: PropTypes.string
};

EnquiryActivityType.defaultProps = {
    categoryHint: null,
    valueHint: null
};

export default EnquiryActivityType;

export { EnquiryActivityType, CHOOSE_OPTION };
